import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

const CTX_EVENT_PARAM = 'ctx_event';
const STORAGE_KEY_PREFIX = 'page_context_events';

export interface PageEntityContext {
  campaignId?: number;
  landingId?: number;
  eventId?: number;
}

const EMPTY_ENTITY_CONTEXT: PageEntityContext = {};

export interface PageFilters {
  eventIds: string[];
}

const EMPTY_FILTERS: PageFilters = {
  eventIds: [],
};

interface PageContextType {
  filters: PageFilters;
  setEventIds: (ids: string[]) => void;
  clearFilters: () => void;
  hasEventFilter: boolean;
  entityContext: PageEntityContext;
  setEntityContext: (ctx: PageEntityContext) => void;
}

export const PageContext = createContext<PageContextType>({
  filters: EMPTY_FILTERS,
  setEventIds: () => {},
  clearFilters: () => {},
  hasEventFilter: false,
  entityContext: EMPTY_ENTITY_CONTEXT,
  setEntityContext: () => {},
});

export const usePageContext = () => useContext(PageContext);

/**
 * Register entity context for the current page.
 * Automatically cleans up on unmount.
 *
 * Usage:
 *   useRegisterPageContext({ campaignId: 123 });
 *   useRegisterPageContext({ landingId: 67 });
 */
export const useRegisterPageContext = (ctx: PageEntityContext) => {
  const { setEntityContext } = usePageContext();

  useEffect(() => {
    setEntityContext(ctx);
    return () => setEntityContext(EMPTY_ENTITY_CONTEXT);
  }, [JSON.stringify(ctx)]); // eslint-disable-line react-hooks/exhaustive-deps
};

interface PageProviderProps {
  children: ReactNode;
}

const parseEventIds = (raw: string | null): string[] => {
  if (!raw) return [];
  return raw.split(',').filter(Boolean);
};

const serializeEventIds = (ids: string[]): string => ids.join(',');

export const PageProvider: React.FC<PageProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [entityContext, setEntityContext] = useState<PageEntityContext>(EMPTY_ENTITY_CONTEXT);
  const storageKey = user?.id ? `${STORAGE_KEY_PREFIX}_${user.id}` : STORAGE_KEY_PREFIX;

  const [filters, setFilters] = useState<PageFilters>(() => {
    const urlRaw = searchParams.get(CTX_EVENT_PARAM);
    if (urlRaw) {
      const ids = parseEventIds(urlRaw);
      localStorage.setItem(storageKey, JSON.stringify(ids));
      return { eventIds: ids };
    }
    const sessionRaw = localStorage.getItem(storageKey);
    if (sessionRaw) {
      try {
        const ids = JSON.parse(sessionRaw) as string[];
        return { eventIds: Array.isArray(ids) ? ids : [] };
      } catch {
        return EMPTY_FILTERS;
      }
    }
    return EMPTY_FILTERS;
  });

  // Reset filters when user changes (admin switching between users). The
  // user loads after the first render, so this also runs on a fresh page
  // load — an event named in the URL (a deep link) wins over the stored pick
  useEffect(() => {
    const urlIds = parseEventIds(searchParams.get(CTX_EVENT_PARAM));
    if (urlIds.length > 0) {
      setFilters({ eventIds: urlIds });
      localStorage.setItem(storageKey, JSON.stringify(urlIds));
      return;
    }

    const sessionRaw = localStorage.getItem(storageKey);
    if (sessionRaw) {
      try {
        const ids = JSON.parse(sessionRaw) as string[];
        setFilters({ eventIds: Array.isArray(ids) ? ids : [] });
        return;
      } catch {
        // fall through
      }
    }
    setFilters(EMPTY_FILTERS);
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync URL → state when URL changes externally (back/forward, manual edit)
  useEffect(() => {
    const urlRaw = searchParams.get(CTX_EVENT_PARAM);
    const urlIds = parseEventIds(urlRaw);
    const same =
      urlIds.length === filters.eventIds.length &&
      urlIds.every((id, i) => id === filters.eventIds[i]);
    if (!same && urlIds.length > 0) {
      setFilters({ eventIds: urlIds });
      localStorage.setItem(storageKey, JSON.stringify(urlIds));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncToUrl = useCallback(
    (ids: string[]) => {
      const params = new URLSearchParams(searchParams);
      if (ids.length > 0) {
        params.set(CTX_EVENT_PARAM, serializeEventIds(ids));
      } else {
        params.delete(CTX_EVENT_PARAM);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setEventIds = useCallback(
    (ids: string[]) => {
      setFilters({ eventIds: ids });
      syncToUrl(ids);
      if (ids.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(ids));
      } else {
        localStorage.removeItem(storageKey);
      }
    },
    [syncToUrl, storageKey],
  );

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    syncToUrl([]);
    localStorage.removeItem(storageKey);
  }, [syncToUrl, storageKey]);

  const hasEventFilter = filters.eventIds.length > 0;

  const value = useMemo(
    () => ({
      filters,
      setEventIds,
      clearFilters,
      hasEventFilter,
      entityContext,
      setEntityContext,
    }),
    [filters, setEventIds, clearFilters, hasEventFilter, entityContext],
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
};
